import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, LOGO_URL } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function ChangePassword() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user === null) return <div className="flex h-screen items-center justify-center text-sm text-slate-500">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: password });
      toast.success("Password changed successfully");
      setUser({ ...user, must_reset_password: false });
      navigate("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6">
      <div className="w-full max-w-sm rounded-md border border-slate-200 bg-white p-8 fade-in-up" data-testid="change-password-page">
        <img src={LOGO_URL} alt="DCN" className="h-14 w-auto" />
        <h1 className="mt-4 text-xl font-bold text-slate-900">Set a new password</h1>
        <p className="mt-1 text-sm text-slate-500">
          {user.must_reset_password
            ? "For security, you must change your initial password before continuing."
            : "Choose a new password for your account."}
        </p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Current Password</label>
            <input data-testid="change-current-input" type="password" required value={current}
              onChange={(e) => setCurrent(e.target.value)} placeholder="Your current password"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none transition-colors duration-150 focus:border-slate-900" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">New Password</label>
            <input data-testid="change-new-input" type="password" required minLength={6} value={password}
              onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none transition-colors duration-150 focus:border-slate-900" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Confirm New Password</label>
            <input data-testid="change-confirm-input" type="password" required minLength={6} value={confirm}
              onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat new password"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none transition-colors duration-150 focus:border-slate-900" />
          </div>
          {error && <p data-testid="change-password-error" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
          <button data-testid="change-submit-button" type="submit" disabled={loading}
            className="w-full rounded-md bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-slate-700 disabled:opacity-60">
            {loading ? "Saving…" : "Change password"}
          </button>
        </form>
        <button data-testid="change-logout-button"
          onClick={async () => { await logout(); navigate("/login"); }}
          className="mt-4 block text-sm text-slate-500 underline-offset-4 hover:underline">
          Sign out instead
        </button>
      </div>
    </div>
  );
}
